import { useBemCN } from '@gik/core/utils/bemBlock';
import withComponentErrorBoundary from '@gik/core/utils/withComponentErrorBoundary';
import React from 'react';
import { PageSection } from '@gik/ui/gik/PageSection';
import type { LandingPageHero } from '../types/LandingPageHero';
import { Layout } from '@gik/ui/gik/Layout/Layout';
import { Button } from '@gik/ui/Button';
import { isVideo } from '@gik/core/utils/fileExtensions';
import { LandingPageHeroCard } from '../components/LandingPageHeroCard';
import { isEmpty } from '@gik/core/utils/validator';
import { useCreateAnalyticsController } from '@gik/create/hooks/useCreateAnalyticsController';
import { CreateClickedLocation } from '@gik/create/enums/CreateAnalyticsEnums';
import routes from '@gik/core/routes';
import { navigateTo } from '@/utils/navigateTo';

export interface IHeroSectionProps {
  hero: LandingPageHero;
  backgroundColor?: string;
  backgroundVideo?: string;
  backgroundImage?: string;
  backgroundImageMd?: string;
}

/**
 * https://www.figma.com/design/FT2ninjhNiQOXfnOTaZfqy/%F0%9F%91%A8%F0%9F%8F%BC%E2%80%8D%F0%9F%8E%A4-Jade's-workspace-of-asset-creation?node-id=4696-8681&m=dev
 */
function LandingPageHeroSectionComp({
  hero,
  backgroundColor,
  backgroundVideo,
  backgroundImage,
  backgroundImageMd = backgroundImage,
}: React.PropsWithChildren<IHeroSectionProps>) {
  const bem = useBemCN('landing-page-hero-section');
  const { trackCreateClicked } = useCreateAnalyticsController();

  function onCreateClicked() {
    trackCreateClicked(CreateClickedLocation.LANDINGPAGE_TOP);
    navigateTo(routes.create());
  }

  return (
    <>
      <style>
        {`
        .${bem().className} {
          --background: url(${backgroundImage});
          --background-md: url(${backgroundImageMd});
        }
      `}
      </style>
      <div
        {...bem(null, null, ['tw-relative', 'tw-overflow-hidden'])}
        style={
          !isEmpty(backgroundColor)
            ? {
                background: backgroundColor,
              }
            : {}
        }
      >
        {isVideo(backgroundVideo) && (
          <video autoPlay muted loop {...bem('background', null, ['tw-absolute'])}>
            <source src={backgroundVideo} />
          </video>
        )}
        <PageSection {...bem('header', [{ background: !isEmpty(backgroundImage) }])} asElement={'header'}>
          <LandingPageHeroCard hero={hero}>
            <Layout direction={'column'} align={'center'} gap={8}>
              <Layout gap={4}>
                <Button type={'a'} href={routes.create()} onClick={onCreateClicked} className={'tw-flex-1'}>
                  Start a page
                </Button>
              </Layout>
            </Layout>
          </LandingPageHeroCard>
        </PageSection>
      </div>
    </>
  );
}

export const LandingPageHeroSection = withComponentErrorBoundary(LandingPageHeroSectionComp);
